import React, { useEffect, useState } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Users, Clock, Activity } from 'lucide-react';
import { presentationServerStatus } from '../data/presentationData';
type Language = 'es' | 'en' | 'pt';
const translations = {
  es: {
    tagline: 'Entra a la',
    ultimate: 'Máxima',
    experience: 'Experiencia',
    subtitle: 'Temporada 6 - Jugabilidad Mejorada, Recompensas Épicas, Batallas Legendarias. Únete a miles de guerreros en el servidor privado más avanzado.',
    createAccount: 'Crear Cuenta',
    downloadGame: 'Descargar Juego',
    serverOnline: 'Servidor En Línea',
    players: 'Jugadores',
    uptime: 'Tiempo Activo',
    ping: 'Ping'
  },
  en: {
    tagline: 'Enter the',
    ultimate: 'Ultimate',
    experience: 'Experience',
    subtitle: 'Season 6 - Enhanced Gameplay, Epic Rewards, Legendary Battles. Join thousands of warriors in the most advanced private server.',
    createAccount: 'Create Account',
    downloadGame: 'Download Game',
    serverOnline: 'Server Online',
    players: 'Players',
    uptime: 'Uptime',
    ping: 'Ping'
  },
  pt: {
    tagline: 'Entre na',
    ultimate: 'Máxima',
    experience: 'Experiência',
    subtitle: 'Temporada 6 - Jogabilidade Aprimorada, Recompensas Épicas, Batalhas Lendárias. Junte-se a milhares de guerreiros no servidor privado mais avançado.',
    createAccount: 'Criar Conta',
    downloadGame: 'Baixar Jogo',
    serverOnline: 'Servidor Online',
    players: 'Jogadores',
    uptime: 'Tempo Ativo',
    ping: 'Ping'
  }
};
export function Hero() {
  const {
    scrollY
  } = useScroll();
  const y = useTransform(scrollY, [0, 1000], [0, 300]);
  const [language, setLanguage] = useState<Language>('es');
  const t = translations[language];
  useEffect(() => {
    const savedLang = localStorage.getItem('language') as Language;
    if (savedLang) setLanguage(savedLang);
    const handleLanguageChange = (e: CustomEvent<Language>) => {
      setLanguage(e.detail);
    };
    window.addEventListener('languageChange', handleLanguageChange as EventListener);
    return () => window.removeEventListener('languageChange', handleLanguageChange as EventListener);
  }, []);
  return <section id="home" className="relative min-h-[600px] sm:min-h-[700px] md:min-h-[900px] lg:min-h-[1000px] xl:min-h-screen flex items-center justify-center overflow-hidden" data-id="element-329">
      {/* Parallax Background */}
      <motion.div style={{
      y
    }} className="absolute inset-0 z-0" data-id="element-330">
        <div className="absolute inset-0 bg-cover bg-center bg-no-repeat" style={{
        backgroundImage: 'url("https://cdn.magicpatterns.com/uploads/eYBYRLuoC8pAFP1V97bAhQ/ChatGPT_Image_Mar_25,_2026,_10_00_56_AM.png")'
      }} data-id="element-331" />
        <div className="absolute inset-0 bg-hero-overlay" data-id="element-332" />
        <div className="absolute inset-0 bg-glowBlue/5 mix-blend-overlay" data-id="element-333" />
      </motion.div>

      {/* Floating Particles */}
      <div className="absolute inset-0 z-0 overflow-hidden pointer-events-none" data-id="element-334">
        {[...Array(20)].map((_, i) => <div key={i} className="absolute w-1 h-1 bg-glowBlue rounded-full animate-pulse-slow" style={{
        top: `${Math.random() * 100}%`,
        left: `${Math.random() * 100}%`,
        opacity: Math.random() * 0.5 + 0.2,
        boxShadow: '0 0 10px 2px rgba(74, 158, 255, 0.4)',
        animationDelay: `${Math.random() * 5}s`,
        animationDuration: `${Math.random() * 3 + 2}s`
      }} data-id="element-335" />)}
      </div>

      {/* Main Content */}
      <motion.div className="relative z-10 text-center px-4 max-w-5xl mx-auto flex flex-col items-center mt-20 mb-12 sm:mt-24 sm:mb-16 md:mt-32 md:mb-20 lg:mt-40 lg:mb-24" data-id="element-336">
        <motion.img initial={{
        scale: 0.8,
        opacity: 0,
        y: 20
      }} animate={{
        scale: 1,
        opacity: 1,
        y: 0
      }} transition={{
        duration: 1,
        ease: 'easeOut'
      }} src="https://cdn.magicpatterns.com/uploads/5zBYTxmUBGoZjkJtCYSzXk/ChatGPT_Image_Mar_26,_2026,_01_37_29_AM.png" alt="MU Vanguard Season 6" className="w-full max-w-[320px] sm:max-w-[450px] md:max-w-[650px] lg:max-w-[750px] h-auto object-contain mb-4 sm:mb-6 drop-shadow-[0_0_30px_rgba(212,175,55,0.3)] animate-float" data-id="element-337" />

        <motion.h1 initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        delay: 0.3
      }} className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black mb-3 sm:mb-4 tracking-wider uppercase text-white drop-shadow-lg" data-id="element-338">
          {t.tagline} <span className="text-gradient-gold" data-id="element-339">{t.ultimate}</span>{' '}
          {t.experience}
        </motion.h1>

        <motion.p initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        delay: 0.5
      }} className="text-sm sm:text-base md:text-lg lg:text-xl text-gray-300 mb-8 sm:mb-10 max-w-2xl mx-auto font-light px-4" data-id="element-340">
          {t.subtitle}
        </motion.p>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        delay: 0.7
      }} className="flex flex-col sm:flex-row gap-4 sm:gap-6 justify-center w-full sm:w-auto px-4 mb-8 sm:mb-12" data-id="element-341">
          <a href="#register" className="btn-primary text-lg px-10 py-4 w-full sm:w-auto text-center" data-id="element-342">
            {t.createAccount}
          </a>
          <a href="#download" className="btn-secondary text-lg px-10 py-4 w-full sm:w-auto text-center" data-id="element-343">
            {t.downloadGame}
          </a>
        </motion.div>

        {/* Server Status Bar */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        delay: 1
      }} className="glass-panel border border-white/10 rounded-xl py-3 sm:py-4 px-4 max-w-4xl mx-auto" data-id="element-344">
          <div className="flex flex-wrap justify-center md:justify-between items-center gap-3 sm:gap-4 md:gap-6 text-xs sm:text-sm md:text-base" data-id="element-345">
            <div className="flex items-center gap-2 sm:gap-3" data-testid="server-status" data-field="server.health" data-id="element-346">
              <div className="relative flex h-2 w-2 sm:h-3 sm:w-3" data-id="element-347">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-yellow-400 opacity-75" data-id="element-348"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 sm:h-3 sm:w-3 bg-yellow-500" data-id="element-349"></span>
              </div>
              <span data-testid="server-status-label" className="font-cinzel font-bold text-yellow-400 text-xs sm:text-sm" data-id="element-350">
                {presentationServerStatus.label}
              </span>
            </div>

            <div className="flex items-center gap-4 sm:gap-6 md:gap-8" data-id="element-351">
              <div className="flex items-center gap-1 sm:gap-2 text-gray-300" data-id="element-352">
                <Users className="text-gold w-4 h-4 sm:w-5 sm:h-5" data-id="element-353" />
                <span className="text-xs sm:text-sm" data-id="element-354">
                  <strong data-testid="online-players" data-field="server.onlinePlayers" className="text-white" data-id="element-355">
                    {presentationServerStatus.onlinePlayers ?? '—'}
                  </strong>{' '}
                  {t.players}
                </span>
              </div>
              <div className="flex items-center gap-1 sm:gap-2 text-gray-300 hidden sm:flex" data-id="element-356">
                <Clock className="text-gold w-4 h-4 sm:w-5 sm:h-5" data-id="element-357" />
                <span className="text-xs sm:text-sm" data-id="element-358">
                  {t.uptime}:{' '}
                  <strong data-testid="server-uptime" data-field="server.uptimePercent" className="text-white" data-id="element-359">
                    {presentationServerStatus.uptimePercent ?? '—'}%
                  </strong>
                </span>
              </div>
              <div className="flex items-center gap-1 sm:gap-2 text-gray-300 hidden md:flex" data-id="element-360">
                <Activity className="text-gold w-4 h-4 sm:w-5 sm:h-5" data-id="element-361" />
                <span className="text-xs sm:text-sm" data-id="element-362">
                  {t.ping}:{' '}
                  <strong data-testid="server-latency" data-field="server.latencyMs" className="text-green-400" data-id="element-363">
                    {presentationServerStatus.latencyMs ?? '—'}ms
                  </strong>
                </span>
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </section>;
}